import styled from '@emotion/styled'
import React, { useMemo, useCallback } from 'react'
import {
    ApiShopperLandingPageLink,
    SelectedOption,
    hasSwatchData,
    buildOptionKeyMap,
    VLPOption,
    hasAnyAvailableVariantForOption,
} from '@storefronts/common-types'
import {
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_BORDER_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { scaledFontSize } from '../../../style/scrollingLayout'

const SWATCH_SIZE = 24
const SWATCH_SIZE_MOBILE = 20
const SWATCH_CONTAINER_SIZE = SWATCH_SIZE + 6
const SWATCH_CONTAINER_SIZE_MOBILE = SWATCH_SIZE_MOBILE + 6

const SwatchesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    margin: 4px 0;
`

const SwatchButton = styled.button<{
    isActive: boolean
    isAvailable: boolean
    colorValue?: string
    imageUrl?: string
}>`
    width: ${SWATCH_CONTAINER_SIZE}px;
    height: ${SWATCH_CONTAINER_SIZE}px;
    border-radius: 50%;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${(props) => (props.isAvailable ? 'pointer' : 'not-allowed')};
    transition: all 0.15s ease;
    border: ${(props) =>
        props.isActive
            ? `1px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
            : '1px solid transparent'};
    background: transparent;
    position: relative;

    &:hover {
        border-color: ${(props) =>
            props.isAvailable
                ? `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
                : 'transparent'};
    }

    &:focus-visible {
        outline: 2px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
        outline-offset: 2px;
    }

    @media (max-width: 768px) {
        width: ${SWATCH_CONTAINER_SIZE_MOBILE}px;
        height: ${SWATCH_CONTAINER_SIZE_MOBILE}px;
    }
`

const SwatchInner = styled.div<{
    colorValue?: string
    imageUrl?: string
    isAvailable: boolean
}>`
    width: ${SWATCH_SIZE}px;
    height: ${SWATCH_SIZE}px;
    border-radius: 50%;
    background: ${(props) =>
        props.imageUrl
            ? `url(${props.imageUrl}) center/cover`
            : props.colorValue || '#ccc'};
    border: 1px solid var(${SUBTLE_BORDER_COLOR_VAR});
    position: relative;
    opacity: ${(props) => (props.isAvailable ? 1 : 0.4)};

    ${(props) =>
        !props.isAvailable &&
        `
        &::after {
            content: "";
            position: absolute;
            top: 50%;
            left: -1px;
            right: -1px;
            height: 1px;
            background: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
            transform: rotate(-45deg);
            opacity: 0.5;
        }
    `}

    @media (max-width: 768px) {
        width: ${SWATCH_SIZE_MOBILE}px;
        height: ${SWATCH_SIZE_MOBILE}px;
    }
`

const MoreSwatchesIndicator = styled.span`
    font-size: ${scaledFontSize(11)};
    color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    opacity: 0.7;
    margin-left: 2px;
`

interface ProductGridColorSwatchesProps {
    lpLink: ApiShopperLandingPageLink
    selectedOptions: SelectedOption[]
    onOptionSelect: (optionName: string, optionValue: string) => void
    maxSwatches?: number
}

export const findProductGridColorSwatchesFromOptionsList = (
    options: VLPOption[],
): VLPOption | null => {
    return (
        options.find((opt) => hasSwatchData(opt) && opt.values.length > 1) ||
        null
    )
}

export const findProductGridColorSwatchesOption = (
    lpLink: ApiShopperLandingPageLink,
): VLPOption | null => {
    return findProductGridColorSwatchesFromOptionsList(lpLink.optionInfo || [])
}

export const getNumberOfAvailableSwatchOptions = (
    lpLink: ApiShopperLandingPageLink,
): number => {
    const swatchOption = findProductGridColorSwatchesOption(lpLink)
    const variants = lpLink.variantInfo || []
    const options = lpLink.optionInfo || []
    if (!swatchOption || variants.length === 0) return 0
    const optionKeyMap = buildOptionKeyMap(options)
    return swatchOption.values.filter(
        ({ value, color, image }) =>
            (!!color || !!image) &&
            hasAnyAvailableVariantForOption({
                variants,
                optionName: swatchOption.name,
                optionValue: value,
                optionKeyMap,
            }),
    ).length
}

export const ProductGridColorSwatches: React.FC<
    ProductGridColorSwatchesProps
> = ({ lpLink, selectedOptions, onOptionSelect, maxSwatches = 5 }) => {
    const variants = useMemo(
        () => lpLink.variantInfo || [],
        [lpLink.variantInfo],
    )
    const optionKeyMap = useMemo(
        () => buildOptionKeyMap(lpLink.optionInfo || []),
        [lpLink.optionInfo],
    )
    const colorOption = useMemo(() => {
        return findProductGridColorSwatchesOption(lpLink)
    }, [lpLink])

    const handleSwatchClick = useCallback(
        (e: React.MouseEvent, optionName: string, optionValue: string) => {
            e.stopPropagation()
            onOptionSelect(optionName, optionValue)
        },
        [onOptionSelect],
    )

    const currentSelection = selectedOptions.find(
        (opt) => opt.name === colorOption?.name,
    )

    // filter out the swatch options that are not available in any variant.
    const availableSwatchOptions = useMemo(() => {
        if (!colorOption) return []
        if (variants.length === 0) return []
        return (
            colorOption?.values.filter(
                ({ value, color, image }) =>
                    (!!color || !!image) &&
                    hasAnyAvailableVariantForOption({
                        variants,
                        optionName: colorOption.name,
                        optionValue: value,
                        optionKeyMap,
                    }),
            ) ?? []
        )
    }, [colorOption, variants, optionKeyMap])

    // Set once: initial value for sort order. Prefer current selection if available, else first available value.
    const initialSelectedValueRef = React.useRef<string | null>(null)
    if (initialSelectedValueRef.current === null && colorOption) {
        const currentAvailable =
            currentSelection?.value &&
            availableSwatchOptions.some(
                ({ value }) => value === currentSelection.value,
            )
        if (currentAvailable) {
            initialSelectedValueRef.current = currentSelection!.value
        } else {
            initialSelectedValueRef.current =
                availableSwatchOptions[0]?.value ?? null
        }
    }

    // Put the initially selected value first when present; otherwise keep order.
    const selected = initialSelectedValueRef.current
    const sortedValues =
        selected != null
            ? (() => {
                  const options = [...availableSwatchOptions]
                  const idx = options.findIndex((o) => o.value === selected)
                  if (idx <= 0) return options
                  const [chosen] = options.splice(idx, 1)
                  return [chosen, ...options]
              })()
            : availableSwatchOptions

    if (!colorOption || sortedValues.length === 0) return null

    const displayedValues = sortedValues.slice(0, maxSwatches)
    const remainingCount = sortedValues.length - maxSwatches

    return (
        <SwatchesContainer
            role="radiogroup"
            aria-label={`${colorOption.name} options`}
            onClick={(e) => e.stopPropagation()}
        >
            {displayedValues.map((valueObj) => {
                const { value, color, image } = valueObj
                const isActive = currentSelection?.value === value
                const isAvailable = hasAnyAvailableVariantForOption({
                    variants,
                    optionName: colorOption.name,
                    optionValue: value,
                    optionKeyMap,
                })

                return (
                    <SwatchButton
                        key={colorOption.name + value}
                        isActive={isActive}
                        isAvailable={isAvailable}
                        onClick={(e) =>
                            isAvailable &&
                            handleSwatchClick(e, colorOption.name, value)
                        }
                        role="radio"
                        aria-checked={isActive}
                        aria-label={`${colorOption.name}: ${value}`}
                        aria-disabled={!isAvailable}
                        title={value}
                    >
                        <SwatchInner
                            colorValue={color}
                            imageUrl={image}
                            isAvailable={isAvailable}
                        />
                    </SwatchButton>
                )
            })}
            {remainingCount > 0 && (
                <MoreSwatchesIndicator>+{remainingCount}</MoreSwatchesIndicator>
            )}
        </SwatchesContainer>
    )
}
